// Bildschirm "Organisation" -- die Namenspflege (ST-PTY-003, zweiter Schnitt;
// ADR-0048).
//
// EIN EIGENER BILDSCHIRM mit GENAU EINER Hauptaktion: die Namen speichern
// (Kapitel 5.2 Nr. 2). Er hat eine eigene Adresse unter
// /kontakte/{partyId}/organisation.
//
// PROGRESSIVE OFFENLEGUNG (5.2.4): das Kontaktdetail zeigt die Namen; die
// Pflege folgt auf Anforderung ueber den Link. FACHSPRACHE (5.2.6):
// "Rechtsname", "Handelsname", "Status" -- kein "legal_name", kein "lock".
// Der Status erscheint LESBAR, aber ohne Bearbeitung: der Organisationsstatus
// ist der Lebenszyklus der Party (Entscheidung Michael, 02.09.2026, ADR-0048),
// und seine regelbasierten Uebergaenge gehoeren CM-PTY-008 -- ein
// Statusauswahlfeld hier waere die Attrappe aus ADR-0002. Branchentyp und
// Klassifikation fehlen mit benanntem Grund (Vokabular nicht beschlossen).
//
// SICHERE FEHLERERHOLUNG (NFR-UX-005, CM-UX-004): ein Konflikt bietet "neu
// laden" und kein "erneut versuchen"; eine abgewiesene Eingabe bleibt stehen
// und ist am Feld markiert. Der Bildschirm erklaert VOR dem Speichern, welcher
// Name in Listen erscheint -- der Handelsname fuehrt, wenn einer da ist.
//
// BARRIEREFREIHEIT (NFR-ACC-001): genau EINE Statusregion mit aria-live, der
// Fokus wandert nach dem Befehl dorthin, Zustaende in Worten.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Field,
  Input,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import {
  confirmationFor,
  renameOrganization,
  validationMessageFor,
} from '../organization.ts';
import { fetchParty, partyHref, type PartyDetail } from '../parties.ts';
import { Brotkrume } from '../shell/brotkrume.tsx';
import { useUeberschriftImInhalt } from '../shell/layout.tsx';
import { StatusPille } from '../shell/status-pille.tsx';
import { meldungsIntent } from '../shell/use-section-commands.ts';
import { PflegeKopf } from './pflege-kopf.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { Zustand } from '../shell/zustand.tsx';

const useStyles = makeStyles({
  kopf: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '0.75rem', marginTop: '0.5rem' },
  hinweis: { display: 'block', marginTop: '0.5rem', color: tokens.colorNeutralForeground2 },
  feld: { width: '100%' },
});

/** Die Anmeldung liess sich vor dem Senden nicht holen: sicher nicht gespeichert, die Eingabe steht noch. */
const TOKEN_FEHLER_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Namen wurden nicht gespeichert.',
  explanation: 'Deine Anmeldung ließ sich gerade nicht erneuern. Gesendet wurde nichts.',
  nextStep: 'Deine Eingabe steht noch im Formular. Speichere sie erneut.',
  retryable: true,
});

type Status = 'laedt' | 'geladen' | 'unbekannt' | 'abgelehnt';

export interface OrganisationScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly partyId: string;
}

export function OrganisationScreen({
  apiBaseUrl,
  accessToken,
  onReauthenticate,
  partyId,
}: OrganisationScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const [party, setParty] = useState<PartyDetail | undefined>(undefined);
  const [status, setStatus] = useState<Status>('laedt');
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  // Ob die Meldung einen offenen Ausgang nennt; gesetzt dort, wo der Satz gewaehlt wird.
  const [offen, setOffen] = useState(false);
  const [bestaetigung, setBestaetigung] = useState<string | undefined>(undefined);
  const [feldfehler, setFeldfehler] = useState<readonly { readonly fieldPath: string; readonly reasonCode: string }[]>([]);
  const [rechtsname, setRechtsname] = useState('');
  const [handelsname, setHandelsname] = useState('');
  const [lockVersion, setLockVersion] = useState(0);
  const [busy, setBusy] = useState(false);

  const statusRef = useRef<HTMLDivElement | null>(null);
  const [fokusAnfordern, setFokusAnfordern] = useState(0);
  const laufRef = useRef(0);

  useEffect(() => {
    if (fokusAnfordern > 0) {
      statusRef.current?.focus();
    }
  }, [fokusAnfordern]);

  const load = useCallback(
    async (stillschweigend = false) => {
      const meinLauf = laufRef.current + 1;
      laufRef.current = meinLauf;
      const gueltig = (): boolean => laufRef.current === meinLauf;
      if (!stillschweigend) {
        setStatus('laedt');
        // Ein ausdrueckliches Neuladen raeumt die alte Meldung ab -- die Lehre
        // aus dem Cloud-Review an #220; das stille Nachladen behaelt sie.
        setOffen(false);
        setMessage(undefined);
        setBestaetigung(undefined);
      }
      try {
        const token = await accessToken();
        if (!gueltig()) {
          return;
        }
        if (token === undefined) {
          onReauthenticate();
          return;
        }
        const result = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId });
        if (!gueltig()) {
          return;
        }
        if (result.outcome === 'not-found' || (result.outcome === 'loaded' && result.party.organization === undefined)) {
          // EIN AUSGANG FUER DREI LAGEN: gibt es nicht, nicht sichtbar, oder
          // eine Person -- fuer diesen Bildschirm gleichbedeutend, und die
          // Meldung vermutet nichts (CM-SEC-003). Den Namen einer sichtbaren
          // Person traegt nur die Brotkrume: er verraet nichts, was der Kontakt nicht zeigt.
          setParty(result.outcome === 'loaded' ? result.party : undefined);
          setStatus('unbekannt');
          return;
        }
        if (result.outcome === 'rejected') {
          setOffen(false);
          setMessage(result.message);
          setStatus('abgelehnt');
          return;
        }
        if ((result.party.organization?.lockVersion ?? 0) < 1) {
          // Eine 200 ohne brauchbaren Sperrstand ist keine Auskunft von
          // LIAISON (Befund des Codex-Cloud-Reviews an #226): der Befehl
          // wiese die 0 als 422 ab, und die Meldung zeigte auf ein Feld, das
          // es am Bildschirm nicht gibt -- eine Sackgasse. Stattdessen die
          // Stoerungslage mit Wiederholweg.
          setOffen(false);
          setMessage({
            headline: 'Die Antwort von LIAISON war unvollständig.',
            explanation: 'Das Profil kam ohne den Stand, den das Speichern braucht.',
            nextStep: 'Versuche es erneut. Bleibt es dabei, nenne der Administration die Vorgangsnummer aus der technischen Sicht.',
            retryable: true,
          });
          setStatus('abgelehnt');
          return;
        }
        setParty(result.party);
        setRechtsname(result.party.organization?.legalName ?? '');
        setHandelsname(result.party.organization?.tradeName ?? '');
        setLockVersion(result.party.organization?.lockVersion ?? 0);
        setStatus('geladen');
      } catch {
        if (!gueltig()) {
          return;
        }
        setOffen(false);
        setMessage({
          headline: 'LIAISON ist gerade nicht erreichbar.',
          explanation: 'Die Organisation ließ sich nicht laden.',
          nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
          retryable: true,
        });
        setStatus('abgelehnt');
      }
    },
    [accessToken, apiBaseUrl, onReauthenticate, partyId],
  );

  useEffect(() => {
    void load();
    return () => {
      laufRef.current += 1;
    };
  }, [load]);

  const speichern = useCallback(async () => {
    setBusy(true);
    setMessage(undefined);
    setOffen(false);
    setBestaetigung(undefined);
    setFeldfehler([]);
    try {
      let token: string | undefined;
      try {
        token = await accessToken();
      } catch {
        // Gesendet ist noch nichts: die Eingabe bleibt, "Namen speichern" ist der sichere
        // Wiederholweg. "Profil neu laden" wuerde die ungespeicherten Namen verwerfen.
        setMessage(TOKEN_FEHLER_MESSAGE);
        return;
      }
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await renameOrganization({
        baseUrl: apiBaseUrl,
        accessToken: token,
        partyId,
        legalName: rechtsname,
        tradeName: handelsname,
        lockVersion,
      });
      if (result.outcome === 'applied') {
        setBestaetigung(confirmationFor(result));
        setLockVersion(result.lockVersion);
        // Stillschweigend nachladen, damit Kopf und Felder den neuen Stand
        // zeigen, ohne die Bestaetigung mitzunehmen.
        await load(true);
      } else if (result.outcome === 'invalid') {
        setFeldfehler(result.errors);
      } else {
        setMessage(result.message);
        // BEIM KONFLIKT BLEIBT DIE EINGABE STEHEN (Hinweis des
        // Codex-Erstreviews vom 03.09.): ein stilles Nachladen ersetzte die
        // ungespeicherten Namen durch den Serverstand -- das Gegenteil der
        // Meldung "lade neu und entscheide dann". Das Neuladen ist der
        // ausdrueckliche Knopf "Profil neu laden"; erst er verwirft die
        // Eingabe sichtbar. Nur der 404 laedt still nach -- dort gibt es
        // keine Eingabe mehr zu verlieren, der Bildschirm wechselt in die
        // Unbekannt-Lage.
        if (result.outcome === 'not-found') {
          await load(true);
        }
      }
    } catch {
      // Ob gespeichert wurde, ist offen. retryable false: dann steht "Profil neu laden" da,
      // und eine Wiederholung mit dem alten Stand liefe in einen Konflikt.
      setOffen(true);
      setMessage({
        headline: 'Ob die Namen gespeichert wurden, ist offen.',
        explanation: 'Die Antwort kam nicht an.',
        nextStep: 'Lade das Profil neu. Dann siehst du, was gilt.',
        retryable: false,
      });
    } finally {
      setBusy(false);
      setFokusAnfordern((zahl) => zahl + 1);
    }
  }, [accessToken, apiBaseUrl, handelsname, load, lockVersion, onReauthenticate, partyId, rechtsname]);

  const rechtsnameFehler = feldfehler.find((fehler) => fehler.fieldPath === 'organization.legalName');
  const handelsnameFehler = feldfehler.find((fehler) => fehler.fieldPath === 'organization.tradeName');

  if (status === 'unbekannt') {
    return <OrganisationNichtDa partyId={partyId} name={party?.displayName} />;
  }

  return (
    <Card className={styles.card}>
      {/* Brotkrume und h1 wie die uebrigen Pflegeseiten (v4, Muster #499): der
          Rueckweg ist der Name in der Brotkrume und steht VOR der Statusregion;
          die Fokusfuehrung nach einem Befehl haengt an statusRef. */}
      <PflegeKopf partyId={partyId} name={party?.displayName} bereich="Namen" />
      {/* GENAU EINE Statusregion; `tabIndex={-1}` fuer die Fokuswanderung. */}
      <div aria-live="polite" role="status" ref={statusRef} tabIndex={-1}>
        {status === 'laedt' ? <Spinner labelPosition="after" label="Organisation wird geladen" /> : undefined}
        {bestaetigung !== undefined ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Gespeichert</MessageBarTitle>
              <div>{bestaetigung}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {feldfehler.length > 0 ? (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Die Namen konnten nicht übernommen werden.</MessageBarTitle>
              <div>Prüfe die markierten Angaben und versuche es erneut.</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
        {message !== undefined ? (
          // Abgelehnt heisst: das Profil liess sich nicht laden (Fehler). Beim Speichern
          // ist eine Ablehnung ein Fehler und nur ein offener Ausgang eine Warnung.
          <MessageBar intent={status === 'abgelehnt' ? 'error' : meldungsIntent(message, offen)}>
            <MessageBarBody>
              <MessageBarTitle>{message.headline}</MessageBarTitle>
              <div>{message.explanation}</div>
              <div>{message.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>

      {status === 'geladen' && party !== undefined ? (
        <>
          <div className={own.kopf}>
            {/* Der STATUS in Worten, lesbar und ohne Bearbeitung: der
                Organisationsstatus ist der Lebenszyklus der Party (ADR-0048);
                seine Uebergaenge kommen mit eigener Regel, und ein
                Auswahlfeld vorher waere eine Attrappe. Dieselbe Pille wie Liste
                und Kontaktkopf. */}
            <Caption1>Status</Caption1>
            <StatusPille status={party.lifecycleStatus} />
          </div>
          <Caption1 className={own.hinweis}>
            Branchentyp und Buchhandelsklassifikation folgen, sobald ihr Vokabular beschlossen ist — hier erscheint
            bis dahin bewusst kein Eingabefeld.
          </Caption1>

          <form
            className={styles.form}
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              void speichern();
            }}
          >
            <Field
              label="Rechtsname"
              required
              validationMessage={
                rechtsnameFehler === undefined
                  ? null
                  : validationMessageFor('organization.legalName', rechtsnameFehler.reasonCode)
              }
            >
              <Input
                id="org-rechtsname"
                className={own.feld}
                value={rechtsname}
                onChange={(_event, data) => setRechtsname(data.value)}
              />
            </Field>
            <Field
              label="Handelsname"
              hint="Der Name, unter dem die Organisation im Verkehr auftritt. Ist einer geführt, erscheint er in Listen; leer lassen heißt: es gibt keinen."
              validationMessage={
                handelsnameFehler === undefined
                  ? null
                  : validationMessageFor('organization.tradeName', handelsnameFehler.reasonCode)
              }
            >
              <Input
                id="org-handelsname"
                className={own.feld}
                value={handelsname}
                onChange={(_event, data) => setHandelsname(data.value)}
              />
            </Field>
            <div className={styles.actions}>
              <Button appearance="primary" type="submit" disabled={busy || rechtsname.trim().length === 0}>
                {busy ? 'Wird gespeichert …' : 'Namen speichern'}
              </Button>
            </div>
          </form>
        </>
      ) : undefined}

      <div className={styles.actions}>
        {status === 'abgelehnt' && message?.retryable === true ? (
          <Button appearance="primary" onClick={() => void load()}>
            Erneut versuchen
          </Button>
        ) : undefined}
        {message !== undefined && message.retryable === false && status === 'geladen' ? (
          <Button appearance="secondary" onClick={() => void load()}>
            Profil neu laden
          </Button>
        ) : undefined}
      </div>
    </Card>
  );
}

/**
 * Ein Nicht-da-Zustand und keine Warnung (Glossar S6): gespeichert sein kann hier nichts.
 * Der Zustand traegt die h1 (v4 v13), darum meldet die Seite die Ueberschrift im Inhalt;
 * die Brotkrume davor behaelt den Rueckweg mit dem Namen.
 */
function OrganisationNichtDa({ partyId, name }: { readonly partyId: string; readonly name: string | undefined }): ReactElement {
  useUeberschriftImInhalt();
  return (
    <>
      <Brotkrume
        schritte={[
          { label: 'Kontakte', to: '/kontakte' },
          { label: name ?? 'Kontakt', to: partyHref(partyId) },
        ]}
        hier="Namen"
      />
      <Zustand
        art="nicht-da"
        titel="Diese Organisation gibt es hier nicht"
        saetze={['Sie wurde entfernt, die Adresse ist nicht mehr gültig, oder dieser Kontakt ist eine Person.']}
        haupt={name === undefined ? { label: 'Zu den Kontakten', href: '/kontakte' } : { label: 'Zum Kontakt', href: partyHref(partyId) }}
      />
    </>
  );
}
